import { useEffect, useState } from 'react';
import { WifiOff, AlertCircle } from 'lucide-react';
import { cn } from '../../styles/cva-v2';

/**
 * 🌐 OFFLINE INDICATOR - Graceful handling pentru conexiune pierdută
 * 
 * Features:
 * - Detectează automat când pierzi conexiunea
 * - Afișează banner discret sus
 * - Auto-hide când revine conexiunea
 * - Prevent actions când ești offline
 */
export const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Keep banner visible for 2s to show "Back online" message
      setTimeout(() => setShowBanner(false), 2000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowBanner(true);
    };

    // Check initial state
    setShowBanner(!navigator.onLine);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!showBanner && !isOnline) return null;

  // Full screen overlay when offline
  if (!isOnline) {
    return (
      <>
        {/* Banner at top */}
        <div
          className={cn(
            "fixed top-0 left-0 right-0 z-[9998] px-4 py-3",
            "flex items-center justify-center gap-3 text-sm font-medium",
            "bg-amber-500 text-white animate-pulse"
          )}
          role="alert"
          aria-live="assertive"
        >
          <WifiOff size={18} />
          <span>Ești offline - verifică conexiunea la internet</span>
        </div>

        {/* Full screen blocking overlay */}
        <div className="fixed inset-0 z-[9997] bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 m-4 max-w-md text-center">
            <WifiOff className="mx-auto mb-4 text-amber-500" size={48} />
            <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">
              Nu ai conexiune la internet
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Aplicația necesită conexiune la internet pentru a funcționa. 
              Verifică conexiunea și încearcă din nou.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-copper-600 text-white rounded-lg hover:bg-copper-700 transition-colors"
            >
              Reîncearcă
            </button>
          </div>
        </div>
      </>
    );
  }

  // Show success banner when back online
  if (showBanner && isOnline) {
    return (
      <div
        className={cn(
          "fixed top-0 left-0 right-0 z-50 px-4 py-3 transition-all duration-300",
          "flex items-center justify-center gap-3 text-sm font-medium",
          "bg-emerald-500 text-white"
        )}
        role="alert"
        aria-live="assertive"
      >
        <AlertCircle size={18} />
        <span>Conexiune restabilită! Datele se sincronizează...</span>
      </div>
    );
  }

  return null;
};

/**
 * Hook pentru a verifica conexiunea în componente
 */
export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleStatusChange = () => setIsOnline(navigator.onLine);

    window.addEventListener('online', handleStatusChange);
    window.addEventListener('offline', handleStatusChange);

    return () => {
      window.removeEventListener('online', handleStatusChange);
      window.removeEventListener('offline', handleStatusChange);
    };
  }, []);

  return isOnline;
};

/**
 * HOC pentru a dezactiva acțiuni când ești offline
 */
export const withOnlineCheck = <P extends object>(
  Component: React.ComponentType<P>,
  offlineMessage = "Această acțiune necesită conexiune la internet"
) => {
  return (props: P) => {
    const isOnline = useOnlineStatus();

    if (!isOnline) {
      return (
        <div className="p-4 text-center text-amber-600">
          <WifiOff className="mx-auto mb-2" size={24} />
          <p>{offlineMessage}</p>
        </div>
      );
    }

    return <Component {...props} />;
  };
};